import { CircleInfoIcon } from "@xola/icons";
import { flash, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import { nullableNumber } from "common";
import dayjs from "dayjs";
import { isNil } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Schedule } from "types";
import { object } from "yup";
import { api } from "../../../api/api";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { Form } from "../../Common/Form";
import { NumberInlineValuePopover } from "../../Common/NumberInlineValuePopover";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { BlackoutSchedules } from "./BlackoutSchedules";

const validationSchema = object().shape({
    timeslotReservationLimit: nullableNumber().moreThan(0, "should be greater than 0"),
});

export const BlackoutRestriction = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [showReservationLimitPopover, setShowReservationLimitPopover] = useState(false);
    const blackoutSchedules = getSchedulesWithUpdatedDate(seller.blackoutSchedules ?? []);

    const initialValues = {
        timeslotReservationLimit: seller.preferences.booking.timeslotReservationLimit ?? null,
    };

    const { mutateAsync: save } = useMutation(async (payload: any) => {
        try {
            return await api.post(`/users/${seller.id}/preferences/booking`, payload);
        } catch {
            flash.danger("Error updating booking preference");
            return null;
        }
    });

    const handleSubmit = async (values: { timeslotReservationLimit: number | null }) => {
        await save({
            ...(seller.preferences.booking ? seller.preferences.booking : {}),
            timeslotReservationLimit: values.timeslotReservationLimit,
        });
        invalidate("sellers", seller.id);
        flash.success("Successfully updated booking preference");
    };

    return (
        <ContentLayout isWide>
            <XolaFormik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, values, setFieldValue, isSubmitting, handleReset, errors }) => {
                    const reservationError = errors.timeslotReservationLimit && "Count should be greater than 0";
                    const handleSwitchToggle = (value: boolean) => {
                        const timeslotReservationLimit = value ? 0 : null;
                        setFieldValue("timeslotReservationLimit", timeslotReservationLimit);
                    };

                    return (
                        <Form>
                            <h2>Blackouts and Restrictions</h2>

                            <ContentLayout.Section title="Global Blackouts Schedules">
                                <BlackoutSchedules schedules={blackoutSchedules} />
                            </ContentLayout.Section>

                            <hr className="text-gray-light" />

                            <ContentLayout.Section title="Global Restrictions">
                                <span className="text-sm font-bold">Reservation Limit</span>
                                <div className="mt-1 flex items-center">
                                    <Switch.Group>
                                        <Switch
                                            size="medium"
                                            isChecked={!isNil(values.timeslotReservationLimit)}
                                            onChange={handleSwitchToggle}
                                        />
                                        <Switch.Label className="ml-2 mr-0">
                                            <span>Limit the number of reservations per time slot</span>
                                        </Switch.Label>
                                    </Switch.Group>
                                    {isNil(values.timeslotReservationLimit) ? null : (
                                        <>
                                            <span className="mx-1">to</span>
                                            <NumberInlineValuePopover
                                                text={String(values.timeslotReservationLimit || 0)}
                                                value={values.timeslotReservationLimit || 0}
                                                error={reservationError}
                                                isOpen={showReservationLimitPopover}
                                                onOpenChange={(value: boolean) => setShowReservationLimitPopover(value)}
                                                onChange={(value) => setFieldValue("timeslotReservationLimit", value)}
                                            />
                                        </>
                                    )}
                                    <Tooltip
                                        className="ml-1"
                                        content="Ensures that no more than the specified number of reservations can start at the same time across all your listings. This helps limit the number of groups checking in at the same time."
                                    >
                                        <CircleInfoIcon />
                                    </Tooltip>
                                </div>
                            </ContentLayout.Section>

                            <ContentLayout.Footer preferenceSidebar>
                                <SubmitButton isLoading={isSubmitting} onClick={handleSubmit}>
                                    Save
                                </SubmitButton>
                                <SecondaryButton type="reset" onClick={handleReset}>
                                    Cancel
                                </SecondaryButton>
                            </ContentLayout.Footer>

                            <ScrollToFieldError />
                        </Form>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

/**
 * Convert the date to seller timezone and slice the time part to get date object. We are doing this for blackout schedules since the api returns utc timestamp and its too much of a hasel to update the api.
 */

const getSchedulesWithUpdatedDate = (schedules: Schedule[]) => {
    return schedules.map((schedule) => {
        return {
            ...schedule,
            ...(schedule.start ? { start: dayjs.tz(schedule.start).format("YYYY-MM-DD") } : null),
            ...(schedule.end ? { end: dayjs.tz(schedule.end).format("YYYY-MM-DD") } : null),
        };
    });
};
